// ── THE HOT LIST — Ben's editorial layer on the board ───────────────
//
// WHAT THIS IS
// A short, hand-curated list of players whose board price should be nudged up
// (or down) relative to what consensus ADP says. It is deliberately EDITORIAL
// and manual: it is not a recency rule, not a momentum model, and nothing
// writes to it automatically. Ben edits this file; no builder session needed.
//
// HOW TO EDIT
// Add or remove entries in HOT_LIST below. Each entry is:
//
//   { player: "Exact Player Name", multiplier: 1.25, note: "why, and when" }
//
//   player      — the player's name as it appears on the board. Matching is
//                 forgiving about case, periods and apostrophes ("A.J. Brown",
//                 "AJ Brown" and "aj brown" all match the same player), so you
//                 do not have to match punctuation exactly.
//   multiplier  — applied to that player's board price. 1.0 = no change.
//                 >1 makes him more expensive, <1 cheaper. There is no cap
//                 here; sanity is your job, and the report is how you check it.
//   note        — free text. Say WHY and roughly WHEN, so a stale entry is
//                 obvious later. This is the field that stops the list rotting.
//   pos         — OPTIONAL. Only needed to disambiguate two players who share a
//                 name; omit it otherwise.
//
// An entry for a player who is not on the board is ignored silently by the
// price map, but `hotListWarnings()` will name it — the Hot List panel and the
// probes call that so a typo cannot sit here doing nothing forever.
//
// WHERE IT APPLIES
// To the board price, BEFORE the engine sees it, and BEFORE the board is
// renormalized back to budget x teams. That ordering is deliberate and it has a
// consequence worth understanding:
//
//   THE POT IS FIXED. A room has exactly budget x teams dollars in it ($2,000
//   at 10 x $200). Raising one player's price does not add money to the room —
//   it takes it from everyone else, spread thinly across the whole board by the
//   renormalization step. A big multiplier on one player is a small tax on all
//   the others. That is correct behaviour, not a bug, and it is why the list
//   should stay short.
//
// A NOTE ON SCALE, LEARNED THE HARD WAY (chunk 10)
// A multiplier is a poor lever on a CHEAP, UNCONTESTED player, and the reason is
// worth understanding before you reach for one. The board sets what bidders are
// WILLING to pay, not what they must. A player nobody contests sells at a $1
// median whatever his board says — measured, at n=600: x1.25 took Parker
// Washington's board $2 -> $3 and his sale mean $1.66 -> $1.64, and x3.00 took
// his board to $7 and his sale mean to $1.59, while costing Gibbs 8pp of his
// $70+ rate to the conservation tax. Bigger multipliers bought nothing and
// damaged the top of the board.
//
// So: multipliers do real work on players who are already CONTESTED at real
// money, where moving the board moves what rivals will go to. If a cheap player
// looks wrong, the honest read is usually that his RANK is wrong — a board
// problem — or that the deep price shelf is too flat — a config problem. Check
// the distribution, not just the board price, before picking a number.
//
// Empty list = the board is byte-identical to no hot list at all. Proved by
// digest comparison, not asserted (see docs/reference/chunk10-adp-refresh-aug9.md).

// EMPTY, AND THAT IS THE SHIPPING STATE (chunk 11, Aug 9 2026).
//
// The list was seeded in chunk 10 with Parker Washington at x1.25. That entry
// has been REMOVED, and the question it existed to answer is closed:
//
//   $1-2 mid-WRs are REALISTIC. Real auctions price WR30-48 there. Chunk 10
//   showed PW's price reflects month-stable consensus (WR33 since July 10) plus
//   the flat $2 shelf, so the sim is REPRODUCING the market, not missing it.
//   The multiplier was also inert on the thing it was meant to fix: x1.25 moved
//   his board $2 -> $3 and his sale mean $1.66 -> $1.64, and x3.00 still left
//   his sale at $1.59 while costing Gibbs 8pp of his $70+ rate.
//
// No rank overrides and no shelf reshape followed from that — the WR deep-tuple
// shelf stays a named backlog line, not a fix.
//
// THE MECHANISM STAYS. It is the right tool for a genuinely CONTESTED player,
// where a board price actually sets what rivals will pay. It was the wrong tool
// for an uncontested $1 bench body, because raising a price cannot create a
// second bidder. Add entries here when that distinction points the other way.
export const HOT_LIST = [];

// ── lookup ──────────────────────────────────────────────────────────
// Forgiving name match: case-insensitive, and periods/apostrophes ignored, so
// the file can be edited in whatever style is natural. Built once at module
// load; HOT_LIST is a static data file, not something that changes at runtime.

const normalizeName = (s) =>
  String(s || "").toLowerCase().replace(/[.'']/g, "").replace(/\s+/g, " ").trim();

const BY_KEY = new Map();
for (const entry of HOT_LIST) {
  if (!entry || !entry.player) continue;
  const m = Number(entry.multiplier);
  if (!Number.isFinite(m) || m <= 0) continue;      // ignore malformed entries
  const k = normalizeName(entry.player) + (entry.pos ? `|${entry.pos}` : "");
  BY_KEY.set(k, m);
}

/**
 * The multiplier for a player, or null when he is not on the list.
 *
 * Returns null rather than 1 on purpose: the caller SKIPS the multiply
 * entirely when this is null, so an empty (or non-matching) list leaves the
 * board bit-identical rather than merely equal after a x1.0 round trip.
 */
export function hotListMultiplier(name, pos) {
  if (BY_KEY.size === 0) return null;
  const n = normalizeName(name);
  if (pos && BY_KEY.has(`${n}|${pos}`)) return BY_KEY.get(`${n}|${pos}`);
  return BY_KEY.has(n) ? BY_KEY.get(n) : null;
}

/** True when the list is empty — the passthrough case. */
export function hotListIsEmpty() {
  return BY_KEY.size === 0;
}

/**
 * Entries that matched nobody on the supplied board, so a typo'd or retired
 * name surfaces instead of silently doing nothing. `boardNames` is any iterable
 * of player names.
 */
export function hotListWarnings(boardNames) {
  const present = new Set();
  for (const n of boardNames || []) present.add(normalizeName(n));
  return HOT_LIST
    .filter((e) => e && e.player && !present.has(normalizeName(e.player)))
    .map((e) => e.player);
}
